/* Bus detail — the live band and the three concerns above the tabs.
 *
 * Replaces the old page's opening, which was six cards of specification with the
 * bus's live state as one line underneath them. During the school run the only
 * question is "is it moving, who is aboard, is the signal honest" — so that goes
 * first, full width, and the static facts move down into Today.
 *
 * ⚠️ HONESTY RULE — do not weaken this. The green pulse and "last fix Ns ago"
 * must flip to a grey "Last seen …" after STALE_MS of silence. The threshold is
 * the same 60s the dashboard live map uses (bug 36: three surfaces disagreed and
 * a killed app read "live" for over two minutes). A band that implies a bus is
 * reporting when it is not is the exact trust failure the grey state exists to
 * prevent, and it is worse here than anywhere else because this page is where
 * someone looks when a parent has already rung them.
 */

import React from 'react';

/** Silence after which "live" becomes a lie. Mirrors the live-map constant. */
const STALE_MS = 60_000;

export interface BusDetailHeaderProps {
  /** Live position freshness — ms since epoch of the last GPS write, or null. */
  liveLocationUpdatedAt: number | null;
  /** True when a route is running right now. */
  onRoute: boolean;
  /** "morning" | "evening" | null — what kind of run. */
  routeLeg: string | null;
  /** When the running route started, for the elapsed clock. */
  routeStartedAt: Date | null;
  childrenAboard: number;
  childrenTotal: number;
  /** Distance covered on the running route, km. Null when not tracking. */
  liveDistanceKm: number | null;
  /** Open (unread) alerts for this bus. */
  openAlerts: number;
  critAlerts: number;
  warnAlerts: number;
  incidents: number;
  /** Oldest open alert, for "open 3 days". */
  oldestAlertAt: Date | null;
  /** 30-day cost per km for this bus and for the fleet. Null when unknown. */
  costPerKm: number | null;
  fleetCostPerKm: number | null;
  /** 30-day distance, km. */
  km30d: number;
  now: number;
  /** The most recent finished run. Fills the band when the bus is parked —
   *  a band of dashes wastes the best space on the page. */
  lastRun?: {
    started: Date | null;
    finished: Date | null;
    km: number | null;
    childrenTotal: number;
    childrenAccounted: number;
  } | null;
  /** Cargo and lorries carry no children, so the aboard cell is meaningless. */
  hasChildren?: boolean;
}

function ago(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} second${s === 1 ? '' : 's'} ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  return `${Math.round(h / 24)} day${Math.round(h / 24) === 1 ? '' : 's'} ago`;
}

function elapsed(from: Date | null, now: number): string {
  if (!from) return '';
  const m = Math.max(0, Math.round((now - from.getTime()) / 60000));
  if (m < 60) return `${m} min in`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m in`;
}

export default function BusDetailHeader({
  liveLocationUpdatedAt, onRoute, routeLeg, routeStartedAt,
  childrenAboard, childrenTotal, liveDistanceKm,
  openAlerts, critAlerts, warnAlerts, incidents, oldestAlertAt,
  costPerKm, fleetCostPerKm, km30d, now, lastRun, hasChildren = true,
}: BusDetailHeaderProps) {
  const silence = liveLocationUpdatedAt ? now - liveLocationUpdatedAt : Infinity;
  const fresh = silence < STALE_MS;

  const legWord = routeLeg === 'evening' ? 'evening run'
    : routeLeg === 'morning' ? 'morning run'
      : 'route';

  const headline = onRoute
    ? `On the ${legWord}`
    : (liveLocationUpdatedAt ? 'Not on a route' : 'No position on file');

  /* Cost comparison is only worth drawing when both sides exist — a percentage
     against a fleet average of zero is a number that means nothing. */
  const costDelta = (costPerKm != null && fleetCostPerKm != null && fleetCostPerKm > 0)
    ? Math.round(((costPerKm - fleetCostPerKm) / fleetCostPerKm) * 100)
    : null;

  const sevTotal = Math.max(1, critAlerts + warnAlerts + Math.max(0, openAlerts - critAlerts - warnAlerts));
  const infoAlerts = Math.max(0, openAlerts - critAlerts - warnAlerts);

  /* Parked bus → the band becomes its last run. Gabriel's call (2026-07-30):
     the alternative was a band of dashes taking the best space on the page. */
  const showLastRun = !onRoute && !!lastRun?.finished;

  return (
    <>
      <div className="fd-livewrap">
        <div className="fd-live">
          {showLastRun && lastRun ? (
            <>
              <div>
                <span className="fd-kicker">Last run</span>
                <div className="fd-live-big">
                  {lastRun.finished!.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'short' })}
                  <small>finished {lastRun.finished!.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
                </div>
                <p className="fd-live-sub">
                  <span className="fd-livedot fd-livedot-stale" />
                  {liveLocationUpdatedAt ? `Parked · last seen ${ago(silence)}` : 'Not currently on a route'}
                </p>
              </div>
              <div>
                <span className="fd-kicker">{hasChildren ? 'Accounted for' : 'Trip'}</span>
                <div className="fd-live-val">
                  {hasChildren
                    ? <>{lastRun.childrenAccounted}<span style={{ opacity: .6, fontWeight: 500 }}> of {lastRun.childrenTotal}</span></>
                    : 'Completed'}
                </div>
                <p className="fd-live-sub">
                  {hasChildren
                    ? (lastRun.childrenAccounted >= lastRun.childrenTotal
                      ? 'Everyone accounted for'
                      : 'Some children were left unresolved')
                    : 'No roster on this vehicle'}
                </p>
              </div>
              <div>
                <span className="fd-kicker">That run</span>
                <div className="fd-live-val">
                  {lastRun.km != null ? `${lastRun.km.toFixed(1)} km` : '—'}
                </div>
                <p className="fd-live-sub">
                  {lastRun.started && lastRun.finished
                    ? `${Math.max(1, Math.round((lastRun.finished.getTime() - lastRun.started.getTime()) / 60000))} minutes`
                    : 'Duration not recorded'}
                </p>
              </div>
              <div>
                <span className="fd-kicker">Last 30 days</span>
                <div className="fd-live-val">{Math.round(km30d)} km</div>
                <p className="fd-live-sub">Across every finished route</p>
              </div>
            </>
          ) : (
            <>
              <div>
                <span className="fd-kicker">Right now</span>
                <div className="fd-live-big">
                  {headline}
                  {onRoute && routeStartedAt && <small>{elapsed(routeStartedAt, now)}</small>}
                </div>
                <p className="fd-live-sub">
                  {fresh
                    ? <><span className="fd-livedot" />Last fix {ago(silence)} · reporting normally</>
                    : (liveLocationUpdatedAt
                      ? <><span className="fd-livedot fd-livedot-stale" />Last seen {ago(silence)}</>
                      : <><span className="fd-livedot fd-livedot-stale" />This bus has never reported a position</>)}
                </p>
              </div>

              {hasChildren && (
                <div>
                  <span className="fd-kicker">Aboard</span>
                  <div className="fd-live-val">
                    {childrenAboard}
                    <span style={{ opacity: .6, fontWeight: 500 }}> of {childrenTotal}</span>
                  </div>
                  <p className="fd-live-sub">
                    {onRoute
                      ? `${Math.max(0, childrenTotal - childrenAboard)} still to ${routeLeg === 'evening' ? 'drop' : 'collect'}`
                      : 'Assigned to this bus'}
                  </p>
                </div>
              )}

              <div>
                <span className="fd-kicker">{onRoute ? 'This route' : 'Last 30 days'}</span>
                <div className="fd-live-val">
                  {onRoute && liveDistanceKm != null
                    ? `${liveDistanceKm.toFixed(1)} km`
                    : `${Math.round(km30d)} km`}
                </div>
                <p className="fd-live-sub">GPS odometer</p>
              </div>

              <div>
                <span className="fd-kicker">Started</span>
                <div className="fd-live-val" style={{ fontSize: 16 }}>
                  {onRoute && routeStartedAt
                    ? routeStartedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : '—'}
                </div>
                <p className="fd-live-sub">{onRoute ? 'This route' : 'No route running'}</p>
              </div>
            </>
          )}
        </div>
      </div>

      <section className="fd-strip fd-strip-3">
        <div className="fd-cell">
          <span className="fd-kicker">Safety</span>
          <div className="fd-big fd-num">
            {openAlerts}
            <small>open alert{openAlerts === 1 ? '' : 's'}{incidents > 0 ? ` · ${incidents} incident${incidents === 1 ? '' : 's'} on file` : ''}</small>
          </div>
          {openAlerts > 0 ? (
            <>
              <div className="fd-sevbar">
                <i style={{ width: `${(critAlerts / sevTotal) * 100}%`, background: 'var(--fd-crit-600)' }} />
                <i style={{ width: `${(warnAlerts / sevTotal) * 100}%`, background: 'var(--fd-warn-600)' }} />
                <i style={{ width: `${(infoAlerts / sevTotal) * 100}%`, background: 'var(--fd-brand-400)' }} />
              </div>
              <p className="fd-cellsub">
                {oldestAlertAt
                  ? `Oldest has been open ${ago(now - oldestAlertAt.getTime()).replace(' ago', '')}`
                  : 'Nothing older than today'}
              </p>
            </>
          ) : (
            <p className="fd-cellsub">Nothing outstanding on this bus</p>
          )}
        </div>

        <div className="fd-cell">
          <span className="fd-kicker">Money</span>
          <div className="fd-big fd-num">
            {costPerKm != null ? `KES ${costPerKm.toFixed(2)}` : '—'}
            <small>
              {costPerKm != null
                ? (fleetCostPerKm != null && fleetCostPerKm > 0
                  ? `per km · fleet averages ${fleetCostPerKm.toFixed(2)}`
                  : 'per km')
                : 'no priced fuel for this bus yet'}
            </small>
          </div>
          <p className="fd-cellsub">
            {costDelta == null
              ? 'Log a refuel, or set a station price, and this fills in'
              : costDelta > 0
                ? `${costDelta}% above the fleet average`
                : costDelta < 0
                  ? `${Math.abs(costDelta)}% below the fleet average`
                  : 'In line with the fleet average'}
          </p>
        </div>

        <div className="fd-cell">
          <span className="fd-kicker">Reporting</span>
          <div className="fd-big fd-num">
            {fresh ? 'Live' : (liveLocationUpdatedAt ? 'Quiet' : '—')}
            <small>{fresh ? 'position arriving normally' : (liveLocationUpdatedAt ? `silent for ${ago(silence).replace(' ago', '')}` : 'never reported')}</small>
          </div>
          <div className="fd-sevbar">
            <i style={{ width: fresh ? '100%' : '0%', background: 'var(--fd-ok-600)' }} />
            <i style={{ width: fresh ? '0%' : '100%', background: 'var(--fd-ink-400)' }} />
          </div>
          <p className="fd-cellsub">
            A bus counts as quiet after 60 seconds of silence, and signal-lost after 5 minutes.
          </p>
        </div>
      </section>
    </>
  );
}
